import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import Cookies from 'js-cookie';
import Home from './Home';
import PlanCarousel from '../components/billing/PlanCarousel';
import ConnectSiteStep from '../components/ConnectSiteStep';
import { subscribeToPlan, upgradeToPlan } from '../utils/subscribeToPlan';
import { syncAllowedPagesCookie } from '../authCookies';
import { resolveWorkflowsLandingPage } from '../utils/pageRoutes';
import { nextSetupStep } from '../utils/setupSteps';
import { capture, planEventName } from '../utils/analytics';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'];
const OTHER_JOB_ROLE = 'Other';
const JOB_ROLES = ['Founder', 'CXO', 'Digital Marketing Head', 'Digital Marketing Manager', OTHER_JOB_ROLE];

const PRIMARY = '#132848';
const GREEN = '#16a34a';
const AMBER = '#d97706';

function StepCircle({ n, current, skipped, onClick }) {
  const done = n < current;
  const active = n === current;
  const isSkipped = done && skipped;
  const isClickable = done; // any completed/skipped step can be revisited

  const bg = isSkipped ? AMBER : done ? GREEN : active ? PRIMARY : '#e5e7eb';
  const color = (done || active) ? 'white' : '#9ca3af';

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      title={isClickable ? 'Click to go back to this step' : undefined}
      style={{
        width: 34, height: 34, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 14,
        backgroundColor: bg, color,
        flexShrink: 0, transition: 'background-color 0.3s, transform 0.15s',
        cursor: isClickable ? 'pointer' : 'default',
        boxShadow: isClickable ? '0 0 0 0 transparent' : 'none',
      }}
      onMouseEnter={(e) => { if (isClickable) e.currentTarget.style.transform = 'scale(1.12)'; }}
      onMouseLeave={(e) => { if (isClickable) e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {isSkipped ? (
        <span style={{ fontSize: 17, lineHeight: 1, fontWeight: 800 }}>!</span>
      ) : done ? (
        <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : n}
    </div>
  );
}

function AccountSetup({ app_url, shop_domain }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [step1, setStep1] = useState({ company_website: '', company_name: '', company_size: '', job_role: '', job_role_other: '' });
  const [errors1, setErrors1] = useState({});

  const [step2Saving, setStep2Saving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [plansForbidden, setPlansForbidden] = useState(false);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [metaConnected, setMetaConnected] = useState(false);
  const [homeKey, setHomeKey] = useState(0);
  const oauthPopupRef = useRef(null);
  const prevGoogleRef = useRef(false);
  const prevMetaRef = useRef(false);
  const remountScheduledRef = useRef(false);
  // tracks whether each completed step was skipped (not fully done)
  const [stepsSkipped, setStepsSkipped] = useState({ 1: false, 2: false, 3: false, 4: false });

  useEffect(() => {
    sessionStorage.setItem('scalex_is_main', '1');
    const onStorage = (e) => {
      if (e.key === 'scalex_oauth_pending' && e.newValue === 'done') {
        localStorage.removeItem('scalex_oauth_pending');
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
          oauthPopupRef.current.close();
          oauthPopupRef.current = null;
        }
      }
      if (e.key === 'scalex_auth_changed' && currentStep === 2) {
        // Auth changed (sign-in popup or sign-out) — update Continue button state
        // Home.jsx's own onAuthChanged listener handles re-fetching the account list
        axios.post(`${app_url}/api/facebook_and_google/get_home_loader_data`,
          { shop_domain: shop_domain },
          { withCredentials: true }
        ).then(({ data }) => {
          if (data?.success) {
            const nowGoogle = !data.data.google.show_google_login_btn;
            const nowMeta = !data.data.facebook.is_show_continue_with_facebook_btn;
            prevGoogleRef.current = nowGoogle;
            prevMetaRef.current = nowMeta;
            setGoogleConnected(nowGoogle);
            setMetaConnected(nowMeta);
          }
        }).catch(() => {});
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      localStorage.removeItem('scalex_oauth_pending');
    };
  }, [currentStep, app_url, shop_domain]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const resp = await axios.get(`${app_url}/api/account-setup/status`, { withCredentials: true });
        if (resp.data?.success) {
          const d = resp.data.data;
          if (d.step1_complete) {
            const savedRole = d.job_role || '';
            const isPresetRole = JOB_ROLES.includes(savedRole);
            setStep1({
              company_website: d.company_website || '',
              company_name: d.company_name || '',
              company_size: d.company_size || '',
              job_role: isPresetRole ? savedRole : (savedRole ? OTHER_JOB_ROLE : ''),
              job_role_other: isPresetRole ? '' : savedRole,
            });
          }
          setStepsSkipped({
            1: false,
            2: d.step2_complete && !!d.step2_skipped,
            3: d.step3_complete && !!d.step3_skipped,
            4: d.step4_complete && !!d.step4_skipped,
          });
          // Accounts that finished before step 4 existed resume at step 4.
          const next = nextSetupStep(d);
          if (next) setCurrentStep(next);
          else navigate('/', { replace: true });
        }
      } catch (_) {
        // start from step 1
      } finally {
        setInitialLoading(false);
      }
    };
    fetchStatus();
  }, [app_url, navigate]);

  const validateStep1 = () => {
    const errs = {};
    if (!step1.company_website.trim()) errs.company_website = 'Website is required';
    if (!step1.company_name.trim()) errs.company_name = 'Company name is required';
    if (!step1.company_size) errs.company_size = 'Select a company size';
    if (!step1.job_role) errs.job_role = 'Select your job role';
    if (step1.job_role === OTHER_JOB_ROLE && !step1.job_role_other.trim()) {
      errs.job_role_other = 'Please enter your job role';
    }
    setErrors1(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    setSaving(true);
    try {
      const payload = {
        ...step1,
        job_role: step1.job_role === OTHER_JOB_ROLE ? step1.job_role_other.trim() : step1.job_role,
      };
      const resp = await axios.post(`${app_url}/api/account-setup/step1`, payload, { withCredentials: true });
      if (resp.data?.success) {
        setCurrentStep(2);
        window.scrollTo(0, 0);
      } else {
        toast.error(resp.data?.message || 'Failed to save');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Poll connection status whenever Step 2 is active so the Continue button
  // reflects what the user just did inside the embedded integration panel.
  useEffect(() => {
    if (currentStep !== 2) return;
    const check = () => {
      axios.post(`${app_url}/api/facebook_and_google/get_home_loader_data`,
        { shop_domain: shop_domain },
        { withCredentials: true }
      ).then(({ data }) => {
        if (data?.success) {
          const nowGoogle = !data.data.google.show_google_login_btn;
          const nowMeta = !data.data.facebook.is_show_continue_with_facebook_btn;
          const googleJustConnected = !prevGoogleRef.current && nowGoogle;
          const metaJustConnected = !prevMetaRef.current && nowMeta;
          prevGoogleRef.current = nowGoogle;
          prevMetaRef.current = nowMeta;
          setGoogleConnected(nowGoogle);
          setMetaConnected(nowMeta);
          if ((googleJustConnected || metaJustConnected) && !remountScheduledRef.current) {
            remountScheduledRef.current = true;
            if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
              oauthPopupRef.current.close();
              oauthPopupRef.current = null;
            }
            setTimeout(() => {
              remountScheduledRef.current = false;
              setHomeKey(k => k + 1);
            }, 500);
          }
        }
      }).catch(() => {});
    };
    check();
    const interval = setInterval(check, 4000);
    return () => clearInterval(interval);
  }, [currentStep, app_url, shop_domain]);

  // Detect popup close without relying on a signal from the OAuth callback page.
  // When the popup closes, wait 2s then remount Home so it re-fetches fresh data.
  useEffect(() => {
    if (currentStep !== 2) return;
    const popupWatcher = setInterval(() => {
      if (oauthPopupRef.current && oauthPopupRef.current.closed) {
        oauthPopupRef.current = null;
        localStorage.removeItem('scalex_oauth_pending');
        if (!remountScheduledRef.current) {
          remountScheduledRef.current = true;
          setTimeout(() => {
            remountScheduledRef.current = false;
            setHomeKey(k => k + 1);
          }, 500);
        }
      }
    }, 500);
    return () => clearInterval(popupWatcher);
  }, [currentStep]);

  const handleStep2Submit = async (skipped = false) => {
    setStep2Saving(true);
    try {
      await axios.post(`${app_url}/api/account-setup/step2`, {
        google_ads_connected: googleConnected,
        skipped,
      }, { withCredentials: true });
      setStepsSkipped(prev => ({ ...prev, 2: skipped }));
      setCurrentStep(3);
      window.scrollTo(0, 0);
    } catch (_) {
      toast.error('Failed to save');
    } finally {
      setStep2Saving(false);
    }
  };

  // Step 3 is plan selection (2026-08-21). The tracking-code installer it
  // replaced left onboarding rather than being renumbered, so `step3_complete`
  // kept its position. It came back on 2026-09-28 as step 4, "Connect your
  // site", after the plan; /settings?tab=setup still has it too.
  useEffect(() => {
    if (currentStep !== 3 || plansLoaded) return;
    let cancelled = false;
    axios.get(`${app_url}/api/cognitive-flows/settings/plans`, { withCredentials: true })
      // `defaultPlan` is also on this response and deliberately unused here:
      // which plan a NEW account is provisioned onto says nothing about what
      // THIS shop holds, and reading it as if it did is what made Get Started
      // skip its own activation.
      .then((r) => { if (!cancelled) setPlans(r.data?.plans || []); })
      .catch((err) => {
        // Billing is owner-only by design. Anyone arriving through the normal
        // signup flow IS the owner (setup-store claims ownership), so this is
        // the admin-provisioned-account case: let them finish rather than
        // stranding them on a step they cannot action.
        if (!cancelled && err?.response?.status === 403) setPlansForbidden(true);
      })
      .finally(() => { if (!cancelled) setPlansLoaded(true); });
    return () => { cancelled = true; };
  }, [currentStep, plansLoaded, app_url]);

  const completeStep3 = async ({ planId = null, skipped = false } = {}) => {
    setSaving(true);
    try {
      await axios.post(`${app_url}/api/account-setup/step3`, {
        selected_plan_id: planId,
        skipped,
      }, { withCredentials: true });

      // The sidebar renders from the `allowed_pages_json` COOKIE, not the
      // database, and the reload when setup finishes re-reads that cookie
      // rather than refetching it. Every paid path already refreshes it inside
      // subscribeToPlan/upgradeToPlan; the free path had nothing, so a customer
      // who skipped landed on a dashboard missing every page their plan grants
      // — present in Mongo, absent from the cookie, until they logged in again.
      await syncAllowedPagesCookie(app_url, shop_domain);

      setStepsSkipped(prev => ({ ...prev, 3: skipped }));
      setCurrentStep(4);
      window.scrollTo(0, 0);
    } catch (_) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Step 4 saves itself (ConnectSiteStep); once it has, setup is finished.
  const finishSetup = ({ skipped }) => {
    if (!skipped) toast.success('Setup complete!');

    // Land on Workflows, not `/`. `/` renders Home, which for a Cognitive
    // Agents account resolves to the integrations page it happens to grant —
    // not what someone who just finished setting up came to do.
    //
    // One full navigation rather than navigate() + reload(): the reload was
    // racing the router, and a hard load is what re-reads the cookie the
    // sidebar renders from anyway.
    const shopKey = String(shop_domain || '').replace('.myshopify.com', '');
    const allowedForShop = (() => {
      try { return JSON.parse(Cookies.get('allowed_pages_json') || '{}')[shopKey] || {}; }
      catch { return {}; }
    })();
    window.location.href = resolveWorkflowsLandingPage(allowedForShop);
  };

  // Every Cognitive Agents plan on offer is a card, free one included. The
  // endpoint already restricts the catalogue to that category, so nothing from
  // the landing-page products can appear here.
  //
  // The free plan is shown as a live choice rather than as "your current plan":
  // it was auto-assigned at signup, the customer never picked it, and a greyed
  // out card is not an answer to "choose a plan". There is no skip link — the
  // free card IS the free route through, and it costs nothing to press.
  const canChoosePlan = plansLoaded && !plansForbidden && plans.length > 0;

  const ctaLabelForPlan = (plan) => (
    Number(plan.chargeMinorUnits) === 0 ? 'Get Started' : 'Choose plan'
  );

  const handleChoosePlan = async (plan) => {
    setSubscribingPlanId(plan.id);
    capture(planEventName(plan, 'clicked'), {
      plan_id: plan.id,
      plan_name: plan.name,
      price: (Number(plan.chargeMinorUnits) || 0) / 100,
      currency: plan.currency,
      surface: 'account_setup',
      action: 'subscribe',
    });
    // subscribeToPlan speaks the subscriptions API's shape; the plans endpoint
    // returns `id` and minor units.
    const args = {
      plan: { _id: plan.id, name: plan.name, price: (plan.chargeMinorUnits || 0) / 100 },
      appUrl: app_url,
      userEmail: Cookies.get('user_email') || '',
      shopDomain: shop_domain,
      notify: {
        success: (m) => toast.success(m),
        error: (m) => toast.error(m),
        info: (m) => toast(m),
      },
    };

    // A free plan activates outright — subscribeToPlan routes it to
    // /activate-free, which is idempotent and opens no checkout, so it succeeds
    // whether the account is already on it, on a legacy plan, or on nothing.
    // Deliberately NOT decided from `isCurrent` here: that flag only resolves
    // for Cognitive Agents plans, so on the 41-of-45 live accounts sitting on a
    // legacy plan a client-side "you already have it" check skipped the
    // activation entirely and the plan was never applied.
    //
    // A paid plan still needs the upgrade fallback: account creation leaves an
    // active subscription behind (assignFreeSubscriptionForNewShop, login.js)
    // and /create refuses a shop that has one.
    let result = await subscribeToPlan(args);
    if (result.status === 'has_active_subscription') {
      result = await upgradeToPlan(args);
    }
    setSubscribingPlanId(null);

    if (result.status === 'activated') {
      // Before completeStep3, which moves the wizard on to step 4.
      capture(planEventName(plan, 'subscribed'), {
        plan_id: plan.id,
        plan_name: plan.name,
        surface: 'account_setup',
        action: Number(plan.chargeMinorUnits) === 0 ? 'get_started' : 'subscribe',
        is_free: Number(plan.chargeMinorUnits) === 0,
        already_active: result.alreadyActive === true,
      });
      await completeStep3({ planId: plan.id, skipped: false });
      return;
    }
    if (result.status === 'cancelled') {
      // Deliberately stay on step 3 — a dismissed payment sheet is not a
      // decision to go without a plan.
      toast.error('Payment cancelled. You can choose a plan again.');
      return;
    }
    toast.error(result.message || 'Could not start your subscription');
  };

  const goToStep = (n) => {
    if (n < currentStep) {
      setCurrentStep(n);
      window.scrollTo(0, 0);
    }
  };

  if (initialLoading) {
    return (
      <div className="main-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Spinner animation="border" style={{ color: PRIMARY }} />
      </div>
    );
  }

  const stepLabels = ['Your Details', 'Ad Accounts', 'Choose a Plan', 'Connect Your Site'];

  return (
    <div className="main-container" style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <Toaster />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 0px 80px' }}>
        <h2 style={{ fontWeight: 700, color: '#101828', marginBottom: 6, fontSize: '1.5rem' }}>Set Up Your Account</h2>
        <p style={{ color: '#667085', marginBottom: 36, fontSize: 14 }}>
          Complete these steps to setup the first campaign 
        </p>

        {/* Step Progress Bar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, marginBottom: 32 }}>
          {[1, 2, 3, 4].map((n) => {
            const isDone = n < currentStep;
            const isSkipped = stepsSkipped[n];
            const connectorColor = isDone
              ? (isSkipped ? AMBER : GREEN)
              : '#e5e7eb';
            return (
              <React.Fragment key={n}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 80 }}>
                  <StepCircle
                    n={n}
                    current={currentStep}
                    skipped={isSkipped}
                    onClick={() => goToStep(n)}
                  />
                  <span style={{
                    fontSize: 12,
                    color: n <= currentStep ? PRIMARY : '#9ca3af',
                    fontWeight: n === currentStep ? 600 : 400,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                    Step {n}<br />
                    <span style={{ fontWeight: 400, color: isDone && isSkipped ? AMBER : '#667085', fontSize: 11 }}>
                      {isDone && isSkipped ? 'Skipped' : stepLabels[n - 1]}
                    </span>
                  </span>
                </div>
                {n < 4 && (
                  <div style={{
                    flex: 1,
                    height: 2,
                    backgroundColor: connectorColor,
                    marginTop: 16,
                    borderRadius: 2,
                    transition: 'background-color 0.3s',
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Step 1: Company Info ── */}
        {currentStep === 1 && (
          <div style={{ background: 'white', borderRadius: 12, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontWeight: 700, color: '#101828', marginBottom: 4, fontSize: '1.15rem', marginTop: 0 }}>Your Details</h3>
            <p style={{ color: '#667085', fontSize: 14, marginBottom: 28 }}>Tell us about your business so we can personalise your experience</p>

            <Form onSubmit={handleStep1Submit} noValidate>
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Your Website *</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://yourcompany.com"
                  value={step1.company_website}
                  onChange={(e) => setStep1(p => ({ ...p, company_website: e.target.value }))}
                  isInvalid={!!errors1.company_website}
                  style={inputStyle}
                />
                <Form.Control.Feedback type="invalid">{errors1.company_website}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Company Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Acme Inc."
                  value={step1.company_name}
                  onChange={(e) => setStep1(p => ({ ...p, company_name: e.target.value }))}
                  isInvalid={!!errors1.company_name}
                  style={inputStyle}
                />
                <Form.Control.Feedback type="invalid">{errors1.company_name}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Company Size *</Form.Label>
                <Form.Select
                  value={step1.company_size}
                  onChange={(e) => setStep1(p => ({ ...p, company_size: e.target.value }))}
                  isInvalid={!!errors1.company_size}
                  style={inputStyle}
                >
                  <option value="">Select company size</option>
                  {COMPANY_SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors1.company_size}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-5">
                <Form.Label style={labelStyle}>Job Role *</Form.Label>
                <Form.Select
                  value={step1.job_role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setStep1((p) => ({
                      ...p,
                      job_role: role,
                      job_role_other: role === OTHER_JOB_ROLE ? p.job_role_other : '',
                    }));
                  }}
                  isInvalid={!!errors1.job_role}
                  style={inputStyle}
                >
                  <option value="">Select your role</option>
                  {JOB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors1.job_role}</Form.Control.Feedback>
              </Form.Group>

              {step1.job_role === OTHER_JOB_ROLE && (
                <Form.Group className="mb-5">
                  <Form.Label style={labelStyle}>Please specify your role *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your job role"
                    value={step1.job_role_other}
                    onChange={(e) => setStep1((p) => ({ ...p, job_role_other: e.target.value }))}
                    isInvalid={!!errors1.job_role_other}
                    style={inputStyle}
                  />
                  <Form.Control.Feedback type="invalid">{errors1.job_role_other}</Form.Control.Feedback>
                </Form.Group>
              )}

              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: PRIMARY, borderColor: PRIMARY, padding: '10px 32px', borderRadius: 8, fontWeight: 600, fontSize: 14 }}
              >
                {saving ? <Spinner size="sm" /> : 'Continue →'}
              </Button>
            </Form>
          </div>
        )}

        {/* ── Step 2: Integrate Ad Accounts ── */}
        {currentStep === 2 && (
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '28px 32px 0' }}>
              <h3 style={{ fontWeight: 700, color: '#101828', marginBottom: 4, fontSize: '1.15rem', marginTop: 0 }}>Integrate Ad Accounts</h3>
              <p style={{ color: '#667085', fontSize: 14, marginBottom: 0 }}>Connect your advertising accounts for personalised recommendations</p>
            </div>

            {/* Embedded Google & Meta integration — same component as Settings > Google & Meta Integration */}
            <Home
              key={homeKey}
              ViewedPage={() => {}}
              shop_domain={shop_domain}
              app_url={app_url}
              isInSettingsContainer={true}
              usePopup={true}
              showAppIntegrations={false}
              onPopupOpen={(popup) => {
                localStorage.setItem('scalex_oauth_pending', 'pending');
                oauthPopupRef.current = popup;
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px 28px', borderTop: '1px solid #f0f0f0' }}>
              <button
                style={{ background: 'none', border: 'none', color: '#667085', fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                onClick={() => handleStep2Submit(true)}
                disabled={step2Saving}
              >
                {step2Saving ? 'Saving...' : 'Skip for now'}
              </button>

              <Button
                onClick={() => handleStep2Submit(false)}
                disabled={(!googleConnected || !metaConnected) || step2Saving}
                style={{
                  backgroundColor: (googleConnected && metaConnected) ? PRIMARY : '#e5e7eb',
                  borderColor: (googleConnected && metaConnected) ? PRIMARY : '#e5e7eb',
                  color: (googleConnected && metaConnected) ? 'white' : '#9ca3af',
                  padding: '10px 32px',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: (googleConnected && metaConnected) ? 'pointer' : 'not-allowed',
                }}
              >
                {step2Saving ? <Spinner size="sm" /> : 'Continue →'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Choose a Plan ── */}
        {currentStep === 3 && (
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '28px 32px 0' }}>
              <h3 style={{ fontWeight: 700, color: '#101828', marginBottom: 4, fontSize: '1.15rem', marginTop: 0 }}>Choose a Plan</h3>
              <p style={{ color: '#667085', fontSize: 14, marginBottom: 0 }}>
                Each plan includes a monthly allowance of runs and AI credits. Unused
                allowance does not carry over; credits you buy on top never expire.
                Start free — you can upgrade any time from Billing.
              </p>
            </div>

            <div style={{ padding: '20px 32px 8px' }}>
              {!plansLoaded && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                  <Spinner animation="border" size="sm" style={{ color: PRIMARY }} />
                </div>
              )}

              {plansLoaded && plansForbidden && (
                <div style={{ background: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: 8, padding: '14px 16px', fontSize: 14, color: '#B54708' }}>
                  Only the account owner can choose a plan. Ask them to pick one from
                  Billing — you can finish setting up in the meantime.
                </div>
              )}

              {plansLoaded && !plansForbidden && plans.length === 0 && (
                <div style={{ background: '#F9FAFB', border: '1px solid #E4E7EC', borderRadius: 8, padding: '14px 16px', fontSize: 14, color: '#667085' }}>
                  No plans are available for your account yet. You can continue and pick one
                  later from Billing.
                </div>
              )}

              {plansLoaded && !plansForbidden && plans.length > 0 && (
                <PlanCarousel
                  plans={plans}
                  onSelect={handleChoosePlan}
                  busyPlanId={subscribingPlanId}
                  accent={PRIMARY}
                  ctaLabel={ctaLabelForPlan}
                  allowCurrent
                />
              )}
            </div>

            {/* No skip. The free card is the free way through and pressing it
                costs nothing, so an underlined "skip" beside it only offered a
                way to leave onboarding with no plan chosen at all.
                A dead end still needs a door, though: when there are no plans to
                choose — none published, or a member who is not the account owner
                — the wizard must not trap them on a step they cannot action. */}
            {plansLoaded && !canChoosePlan && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px 32px 28px', borderTop: '1px solid #f0f0f0' }}>
                <Button
                  onClick={() => completeStep3({ skipped: true })}
                  disabled={saving}
                  style={{ backgroundColor: PRIMARY, borderColor: PRIMARY, padding: '10px 32px', borderRadius: 8, fontWeight: 600, fontSize: 14 }}
                >
                  {saving ? <Spinner size="sm" /> : 'Continue →'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Connect Your Site ── */}
        {currentStep === 4 && (
          <div style={{ background: 'white', borderRadius: 12, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontWeight: 700, color: '#101828', marginBottom: 4, fontSize: '1.15rem', marginTop: 0 }}>Connect Your Site</h3>
            <p style={{ color: '#667085', fontSize: 14, marginBottom: 24 }}>
              Add the snippet to your site, then test the connection. You can skip this for now.
            </p>
            <ConnectSiteStep app_url={app_url} defaultUrl={step1.company_website} onDone={finishSetup} />
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = { fontSize: 14, fontWeight: 500, color: '#344054', marginBottom: 6, display: 'block' };
const inputStyle = { padding: '10px 14px', borderRadius: 8, border: '1px solid #D0D5DD', fontSize: 14, boxShadow: '0 1px 2px rgba(16,24,40,0.05)' };

export default AccountSetup;
